import { styled } from '@mui/material/styles';
import { Box, Button, Typography, Modal, Select, IconButton } from '@mui/material';

// ============================================
// Modal Container & Layout
// ============================================
export const StyledModal = styled(Modal)({});

export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
  maxWidth: '90vw',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 24px',
  height: '48px',
  boxSizing: 'border-box',
  backgroundColor: '#262626',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: 'white',
  fontFamily: 'Pretendard, sans-serif',
});

export const CloseIconWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
});

export const ModalContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '32px',
});

// ============================================
// Dropdown Section
// ============================================
export const DropdownWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '240px',
});

export const StyledSelect = styled(Select)({
  width: '100%',
  height: '44px',
  borderRadius: '4px',
  backgroundColor: '#fff',
  '& .MuiSelect-select': {
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: 'Pretendard, sans-serif',
    fontWeight: 500,
    color: '#262626',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#A6A6A6',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F7F85',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F7F85',
  },
});

// ============================================
// Legend (범례)
// ============================================
export const LegendWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
});

export const LegendItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const LegendColor = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  backgroundColor: bgColor,
}));

export const LegendText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#737373',
  fontFamily: 'Pretendard, sans-serif',
  whiteSpace: 'nowrap',
});

// ============================================
// Locker Grid with Navigation
// ============================================
export const LockerGridContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const NavigationButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'direction',
})(({ disabled }) => ({
  width: '32px',
  height: '32px',
  padding: 0,
  color: disabled ? '#D9D9D9' : '#404040',
  '&:hover': {
    backgroundColor: 'transparent',
    color: disabled ? '#D9D9D9' : '#0F7F85',
  },
  '& svg': {
    width: '32px',
    height: '32px',
  },
}));

export const LockerGridWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const LockerRow = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const LockerItem = styled(Button, {
  shouldForwardProp: prop => !['isInUse', 'isSelected', 'isMine', 'isCurrent'].includes(prop),
})(({ isInUse, isSelected, isMine, isCurrent }) => {
  const getBackgroundColor = () => {
    if (isCurrent) return '#fff'; // 현재 자리 - 흰 배경 + 청록 보더 마커
    if (isSelected) return '#FFB659'; // 선택됨 - 주황색
    if (isMine) return '#5B9BD5'; // 내 라커 - 파란색 (연장 선택 가능)
    if (isInUse) return '#A6A6A6'; // 사용 중 - 회색 (Figma 색상에 맞춤)
    return '#0F7F85'; // 사용 가능 - 청록색
  };

  return {
    minWidth: 'unset',
    width: '40px',
    height: '40px',
    borderRadius: '4px',
    backgroundColor: getBackgroundColor(),
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Pretendard, sans-serif',
    padding: 0,
    lineHeight: 'normal',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: isSelected ? '#E5A24F' : isInUse ? '#A6A6A6' : '#0d6d72',
    },
    '&.Mui-disabled': {
      backgroundColor: getBackgroundColor(),
      color: '#fff',
    },
    // 현재 자리 마커 — 흰 배경 + 청록 보더, 색상 오버라이드가 마지막에 와야 이긴다
    ...(isCurrent && {
      border: '2px solid #0F7F85',
      boxSizing: 'border-box',
      color: '#0F7F85',
      fontWeight: 800,
      fontSize: '13px',
      '&.Mui-disabled': { backgroundColor: '#fff', color: '#0F7F85' },
      '&:hover': { backgroundColor: '#fff' },
    }),
  };
});

// ============================================
// Footer & Buttons
// ============================================
export const ModalFooter = styled(Box)({
  borderTop: '1px solid #E0E0E0',
  padding: '16px 24px',
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
});

export const CancelButton = styled(Button)({
  background: '#D9D9D9',
  color: '#262626',
  borderRadius: '8px',
  padding: '10px 32px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  minWidth: '100px',
  '&:hover': {
    backgroundColor: '#c9c9c9',
  },
});

export const ConfirmButton = styled(Button)(({ disabled }) => ({
  background: disabled ? '#ccc' : '#0F7F85',
  color: 'white',
  borderRadius: '8px',
  padding: '10px 32px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  minWidth: '100px',
  '&:hover': {
    backgroundColor: disabled ? '#ccc' : '#0d6d72',
  },
  '&.Mui-disabled': {
    color: 'white',
    backgroundColor: '#ccc',
  },
}));
